/**
 * Which bill a receipt settled — said only as far as TallyPrime's facts allow
 * (docs/tally/17 §3–§6).
 *
 * ## The four source states, and the one overlay
 *
 *   EXACT_FROM_TALLY              TallyPrime states it: the voucher's party
 *                                 line is "Agst Ref" to named bills.
 *   DERIVED_WITH_HIGH_CONFIDENCE  TallyPrime does not state it through the
 *                                 path Timely read, but the change in its own
 *                                 outstanding bills between two reads admits
 *                                 exactly one explanation.
 *   AMBIGUOUS                     More than one explanation fits. Timely
 *                                 shows the movement and says it cannot tell.
 *   UNALLOCATED_FROM_TALLY        TallyPrime holds the money against no bill:
 *                                 On Account, an advance, a new reference, or
 *                                 a ledger kept without bills.
 *
 *   TIMELY_OPERATIONAL_ALLOCATION is not a source state. It is a person's
 *   allocation for collection work, kept apart (`operationalAllocation.ts`),
 *   and it never changes any of the above.
 *
 * ## What is never done
 *
 * No oldest-first, earliest-due-first or largest-first rule is applied as an
 * accounting allocation — there is none in this file to apply. A receipt's
 * amount is never matched to bills by arithmetic when more than one fit
 * exists. A credit is never moved into a bill because the net is positive.
 *
 * ## Why most of the time nothing needs guessing
 *
 * TallyPrime's bills reports already reflect the accountant's allocation: a
 * receipt entered against INV-001 closes INV-001 in Bills Receivable; one
 * entered On Account leaves every bill open and shows as money in no bill.
 * So the bill-level outcome is observed directly. What can be unclear is only
 * which *voucher* did it, when several things happened between two reads and
 * TallyPrime's vouchers were not read — and then Timely says so.
 */
import { compareFacts, type AccountMovement } from './billMovements.js';
import {
    fromPaise, toPaise,
    type BillAllocationKind, type SourceAccountFact, type SourceFacts, type SourceVoucherFact, type VoucherBillAllocation,
} from './receivablesFacts.js';

export type AllocationStatus = 'EXACT_FROM_TALLY' | 'DERIVED_WITH_HIGH_CONFIDENCE' | 'AMBIGUOUS' | 'UNALLOCATED_FROM_TALLY';
export const TIMELY_OPERATIONAL_ALLOCATION = 'TIMELY_OPERATIONAL_ALLOCATION' as const;
export type AllocationDisplayStatus = AllocationStatus | typeof TIMELY_OPERATIONAL_ALLOCATION;

/** The words for each state, the same on every screen. */
export const ALLOCATION_STATES: Record<AllocationDisplayStatus, { label: string; meaning: string }> = {
    EXACT_FROM_TALLY: {
        label: 'Allocated in TallyPrime',
        meaning: 'TallyPrime states which bill or bills this was entered against.',
    },
    DERIVED_WITH_HIGH_CONFIDENCE: {
        label: 'Derived from TallyPrime',
        meaning: 'Worked out from the change in TallyPrime’s outstanding bills between two updates, where only one explanation fits. TallyPrime did not state it directly.',
    },
    AMBIGUOUS: {
        label: 'Allocation needs review',
        meaning: 'TallyPrime shows this receivable changed, but Timely cannot reliably determine which bill(s) were settled. Timely will not guess.',
    },
    UNALLOCATED_FROM_TALLY: {
        label: 'Unallocated in TallyPrime',
        meaning: 'TallyPrime holds this against no bill. Timely will not guess which bill it is for. Once it is allocated in TallyPrime, Timely updates by itself.',
    },
    TIMELY_OPERATIONAL_ALLOCATION: {
        label: 'Timely allocation for collections',
        meaning: 'Recorded in Timely by a person, for collection work only. TallyPrime and every balance are unchanged.',
    },
};

export type UnallocatedKind = 'on_account' | 'advance' | 'new_reference' | 'credit_reference' | 'not_bill_wise';

export type AllocationExplanation =
    | 'tally_agst_ref'
    | 'tally_partly_allocated'
    | 'tally_on_account'
    | 'tally_advance'
    | 'tally_new_reference'
    | 'tally_lines_do_not_add_up'
    | 'ledger_not_bill_wise'
    | 'derived_single_explanation'
    | 'derived_for_voucher'
    | 'derived_unallocated'
    | 'derived_allocation_applied'
    | 'ambiguous_several_receipts'
    | 'ambiguous_mixed_movement'
    | 'ambiguous_earlier_credit'
    | 'ambiguous_bill_identity'
    | 'ambiguous_amounts_disagree'
    | 'ambiguous_lines_not_read'
    | 'ambiguous_no_voucher';

/** One sentence per explanation, in the product's words. */
export const EXPLANATIONS: Record<AllocationExplanation, string> = {
    tally_agst_ref: 'Entered in TallyPrime against the bills shown.',
    tally_partly_allocated: 'Entered in TallyPrime partly against bills; the rest is unallocated in TallyPrime.',
    tally_on_account: 'Entered in TallyPrime On Account: against no bill.',
    tally_advance: 'Entered in TallyPrime as an advance, against no bill.',
    tally_new_reference: 'Entered in TallyPrime as a new reference, not against an existing bill.',
    tally_lines_do_not_add_up: 'The voucher’s bill lines, as read, do not add up to its amount. Timely will not guess the rest.',
    ledger_not_bill_wise: 'TallyPrime keeps no bills for this ledger, so its receipts are not allocated to bills.',
    derived_single_explanation: 'Worked out from TallyPrime’s outstanding bills: these bills fell by these amounts between two updates, and nothing else changed. TallyPrime did not say how — usually a receipt or credit note, but an invoice deleted or reduced looks the same. If more than one receipt was entered in that time, they are shown together.',
    derived_for_voucher: 'TallyPrime did not give this voucher’s bill lines. They are worked out from its outstanding bills, where only one explanation fits.',
    derived_unallocated: 'The balance fell but no bill changed in TallyPrime: the money is held against no bill.',
    derived_allocation_applied: 'Credit TallyPrime held against no bill is now allocated to these bills in TallyPrime.',
    ambiguous_several_receipts: 'Several receipts arrived between two updates and more than one bill fell. Timely cannot tell which receipt settled which bill.',
    ambiguous_mixed_movement: 'Bills moved both ways between two updates — some fell, some are pending again. TallyPrime may have moved an allocation, or several separate changes happened. Timely cannot tell which.',
    ambiguous_earlier_credit: 'New money and credit TallyPrime already held were both applied to more than one bill. Timely cannot tell which part settled which bill.',
    ambiguous_bill_identity: 'A bill’s reference changed while amounts moved. Timely cannot reliably tell the bills apart.',
    ambiguous_amounts_disagree: 'The receipts read from TallyPrime do not add up to the change in its outstanding bills. Timely will not guess the difference.',
    ambiguous_lines_not_read: 'TallyPrime did not give this voucher’s bill lines, and there is no earlier update to compare with. Timely will not guess.',
    ambiguous_no_voucher: 'TallyPrime’s bills changed between two updates, but no receipt, credit note or journal read from TallyPrime explains it — an invoice deleted or changed looks like this, and so does an entry dated outside the period read. Timely will not guess.',
};

export interface AllocationLine {
    ref: string;
    billKey?: string;
    /** Positive: credit applied to this bill. */
    amount: number;
}

export interface VoucherRef {
    guid: string;
    alterId?: number;
    date: string;
    type: string;
    number?: string;
    postDated: boolean;
    instrument?: SourceVoucherFact['instrument'];
}

export interface CreditEvent {
    /** Deterministic: `v:<guid>:<alterId>` from a voucher, `m:<account>:<read>:<n>` from a movement. */
    id: string;
    sourceKey: string;
    evidence: 'voucher' | 'movement';
    /** `credit`: money or a credit note entered. `allocation_change`: credit already held was applied to bills. */
    kind: 'credit' | 'allocation_change';
    voucher?: VoucherRef;
    /** Movement evidence: the two reads the change happened between. */
    between?: { from: string; to: string };
    /** The voucher date, or the as-of date of the read that first showed it. */
    effectiveDate: string;
    /** Positive. */
    amount: number;
    status: AllocationStatus;
    /** Only for EXACT and DERIVED. Never filled for AMBIGUOUS or UNALLOCATED. */
    allocations: AllocationLine[];
    /** What TallyPrime holds against no bill from this event. */
    unallocated: { kind: UnallocatedKind; ref?: string; amount: number }[];
    explanation: AllocationExplanation;
    /** AMBIGUOUS: the bills that moved, as observed, so the screen can show the facts without choosing between them. */
    observed?: { ref: string; change: number }[];
    /** The event this one replaces: the same voucher, read before it was altered in TallyPrime. Both are kept. */
    supersedes?: string;
}

export interface ReversalEvent {
    id: string;
    sourceKey: string;
    evidence: 'voucher' | 'movement';
    kind: 'bill_reopened' | 'unallocated_credit_removed';
    amount: number;
    bills: { ref: string; billKey?: string; amount: number }[];
    voucher?: VoucherRef;
    between?: { from: string; to: string };
    effectiveDate: string;
}

/** What Timely remembers of a voucher between reads, to notice it change. */
export interface VoucherState {
    guid: string;
    alterId?: number;
    sourceKey: string;
    date: string;
    type: string;
    number?: string;
    partyAmount: number;
    allocations: VoucherBillAllocation[];
    allocationsRead: boolean;
    cancelled: boolean;
    optional: boolean;
    postDated: boolean;
    instrument?: SourceVoucherFact['instrument'];
}

export type SourceChangeKind = 'allocation_changed' | 'amount_changed' | 'date_changed' | 'cancelled' | 'restored' | 'no_longer_in_tally';

export interface SourceChange {
    id: string;
    sourceKey: string;
    voucherGuid: string;
    change: SourceChangeKind;
    before: VoucherState;
    after: VoucherState | null;
}

export interface ReconcileInput {
    /** The previous read, or null on the first. */
    prev: Pick<SourceFacts, 'source' | 'asOf' | 'capturedAt' | 'accounts' | 'voucherWindow'> | null;
    curr: SourceFacts;
    /** The voucher states remembered from earlier reads (any age). */
    knownVouchers?: readonly VoucherState[];
    /**
     * Bills that closed in earlier reads, remembered for a while. TallyPrime's
     * bills reports list only open bills, so without this a bill that is
     * pending again (a receipt reversed) would look like a new sale.
     */
    recentlyClosed?: readonly ClosedBill[];
}

export interface ClosedBill { sourceKey: string; key: string; ref: string; billDate: string; amount: number; closedAt: string }

export interface ReconcileResult {
    credits: CreditEvent[];
    reversals: ReversalEvent[];
    changes: SourceChange[];
    /** The voucher states to remember: every voucher in this read's window, plus those remembered from outside it. */
    voucherStates: VoucherState[];
    /** Movement the vouchers read did not explain, per account (feeds a check). */
    unexplained: { sourceKey: string; observedSettled: number; explainedByVouchers: number }[];
    /** The two reads were of different companies: nothing was compared. */
    companyChanged: boolean;
    /** Debit bills that closed between these reads, to remember (see `recentlyClosed`). */
    closedNow: ClosedBill[];
}

/* ----------------------------- one voucher ------------------------------ */

const voucherRef = (v: VoucherState | SourceVoucherFact): VoucherRef => ({
    guid: v.guid,
    ...(v.alterId !== undefined ? { alterId: v.alterId } : {}),
    date: v.date, type: v.type,
    ...(v.number ? { number: v.number } : {}),
    postDated: v.postDated,
    ...(v.instrument ? { instrument: v.instrument } : {}),
});

const unallocatedKindOf = (k: BillAllocationKind): UnallocatedKind =>
    k === 'on_account' ? 'on_account' : k === 'advance' ? 'advance' : 'new_reference';

const normRef = (ref: string) => ref.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * A voucher that credits a customer, classified from its own bill lines.
 * Null when its lines were not read — the movement must then speak for it.
 */
export function classifyCreditVoucher(v: VoucherState | SourceVoucherFact, account: Pick<SourceAccountFact, 'billWise' | 'bills'> | undefined, effectiveDate: string = v.date): CreditEvent | null {
    const amountP = -toPaise(v.partyAmount);
    if (amountP <= 0 || v.cancelled || v.optional) return null;
    const base = {
        id: `v:${v.guid.toLowerCase()}:${v.alterId ?? 'na'}`,
        sourceKey: v.sourceKey,
        evidence: 'voucher' as const,
        kind: 'credit' as const,
        voucher: voucherRef(v),
        effectiveDate,
        amount: fromPaise(amountP),
    };
    if (account && !account.billWise && v.allocations.length === 0) {
        return { ...base, status: 'UNALLOCATED_FROM_TALLY', allocations: [], unallocated: [{ kind: 'not_bill_wise', amount: fromPaise(amountP) }], explanation: 'ledger_not_bill_wise' };
    }
    if (!v.allocationsRead) return null;
    const linesP = v.allocations.reduce((s, a) => s + toPaise(a.amount), 0);
    if (linesP !== toPaise(v.partyAmount) || v.allocations.some(a => toPaise(a.amount) > 0)) {
        return { ...base, status: 'AMBIGUOUS', allocations: [], unallocated: [], explanation: 'tally_lines_do_not_add_up' };
    }
    // A bill a receipt closed is no longer open, so it is not among the account's bills; its key is still its reference.
    const keyOf = (ref: string) => {
        const found = (account?.bills ?? []).filter(b => normRef(b.ref) === normRef(ref));
        return found.length === 1 ? found[0]!.key : found.length === 0 ? `ref:${normRef(ref)}` : undefined;
    };
    const allocations: AllocationLine[] = [];
    const unallocated: CreditEvent['unallocated'] = [];
    for (const a of v.allocations) {
        const amt = fromPaise(-toPaise(a.amount));
        if (amt === 0) continue;
        if (a.kind === 'agst_ref') {
            const k = keyOf(a.ref);
            allocations.push({ ref: a.ref, ...(k ? { billKey: k } : {}), amount: amt });
        } else unallocated.push({ kind: unallocatedKindOf(a.kind), ...(a.ref ? { ref: a.ref } : {}), amount: amt });
    }
    if (allocations.length) {
        return { ...base, status: 'EXACT_FROM_TALLY', allocations, unallocated, explanation: unallocated.length ? 'tally_partly_allocated' : 'tally_agst_ref' };
    }
    const first = unallocated[0]?.kind ?? 'on_account';
    return {
        ...base, status: 'UNALLOCATED_FROM_TALLY', allocations: [], unallocated,
        explanation: first === 'advance' ? 'tally_advance' : first === 'new_reference' ? 'tally_new_reference' : 'tally_on_account',
    };
}

/* --------------------------- a voucher's effect --------------------------- */

interface Effect { bills: Map<string, { ref: string; amountP: number }>; unbilledP: number; closingP: number }

const emptyEffect = (): Effect => ({ bills: new Map(), unbilledP: 0, closingP: 0 });

/** What a voucher does to its customer's bills, as of a date (nothing before its date, nothing if cancelled or optional). */
function effectAt(v: VoucherState, asOf: string): Effect | null {
    if (v.cancelled || v.optional || v.date > asOf) return emptyEffect();
    if (!v.allocationsRead) return null;
    const e = emptyEffect();
    e.closingP = toPaise(v.partyAmount);
    for (const a of v.allocations) {
        const p = toPaise(a.amount);
        if (a.kind === 'on_account' || !a.ref.trim()) { e.unbilledP += p; continue; }
        const k = normRef(a.ref);
        const prev = e.bills.get(k);
        e.bills.set(k, { ref: a.ref, amountP: (prev?.amountP ?? 0) + p });
    }
    // Lines that do not add up leave the rest in no bill, as TallyPrime would show it.
    const linesP = v.allocations.reduce((s, a) => s + toPaise(a.amount), 0);
    e.unbilledP += e.closingP - linesP;
    return e;
}

/* ------------------------------ movements ------------------------------- */

interface Parts {
    sourceKey: string;
    billWise: boolean;
    settled: { key: string; ref: string; p: number }[];
    reopened: { key: string; ref: string; p: number }[];
    sales: { key: string; ref: string; p: number }[];
    creditAdded: { key: string; ref: string; p: number }[];
    creditUsed: { key: string; ref: string; p: number }[];
    unbilledP: number;
    closingP: number;
    identityUncertain: boolean;
}

function partsOf(m: AccountMovement, expected: Effect | null): Parts {
    const exp = expected ?? emptyEffect();
    const remaining = new Map(exp.bills);
    const parts: Parts = {
        sourceKey: m.sourceKey, billWise: m.billWise, settled: [], reopened: [], sales: [], creditAdded: [], creditUsed: [],
        unbilledP: toPaise(m.unbilledChange) - exp.unbilledP,
        closingP: toPaise(m.closingChange) - exp.closingP,
        identityUncertain: m.identityUncertain,
    };
    for (const d of m.bills) {
        const k = normRef(d.ref);
        const e = remaining.get(k);
        remaining.delete(k);
        const changeP = toPaise(d.change) - (e?.amountP ?? 0);
        if (changeP === 0) continue;
        const entry = { key: d.key, ref: d.ref, p: Math.abs(changeP) };
        if (d.side === 'Dr') {
            if (d.status === 'new' && changeP > 0) parts.sales.push(entry);
            else if (changeP < 0) parts.settled.push(entry);
            else parts.reopened.push(entry);
        } else if (changeP < 0) parts.creditAdded.push(entry);
        else parts.creditUsed.push(entry);
    }
    // Expected bill changes the reads did not show: the vouchers say more happened than the bills do.
    for (const [, e] of remaining) {
        if (e.amountP === 0) continue;
        const entry = { key: `ref:${normRef(e.ref)}`, ref: e.ref, p: Math.abs(e.amountP) };
        if (e.amountP < 0) parts.reopened.push(entry); else parts.settled.push(entry);
    }
    return parts;
}

const sumP = (xs: { p: number }[]) => xs.reduce((s, x) => s + x.p, 0);

function classifyParts(parts: Parts, ctx: { from: string; to: string; asOf: string; seq: () => number; unread: VoucherState[]; vouchersRead: boolean }): { credits: CreditEvent[]; reversals: ReversalEvent[] } {
    const credits: CreditEvent[] = [], reversals: ReversalEvent[] = [];
    const between = { from: ctx.from, to: ctx.to };
    const mId = () => `m:${parts.sourceKey}:${ctx.to}:${ctx.seq()}`;
    const S = sumP(parts.settled), R = sumP(parts.reopened), N = sumP(parts.sales), CU = sumP(parts.creditUsed);
    const uDown = Math.max(0, -parts.unbilledP), uUp = Math.max(0, parts.unbilledP);
    // New credit that entered the account (a receipt, a credit note), net of everything else observed.
    const Q = -(parts.closingP - N - R);
    // Every bill change, signed as TallyPrime's pending moved: shown with an unclear event so the facts are on screen without a choice between them.
    const observed = [
        ...parts.settled.map(x => ({ ref: x.ref, change: fromPaise(-x.p) })),
        ...parts.reopened.map(x => ({ ref: x.ref, change: fromPaise(x.p) })),
        ...parts.sales.map(x => ({ ref: x.ref, change: fromPaise(x.p) })),
        ...parts.creditAdded.map(x => ({ ref: x.ref, change: fromPaise(-x.p) })),
        ...parts.creditUsed.map(x => ({ ref: x.ref, change: fromPaise(x.p) })),
    ];
    // An unclear event's headline: what the balance fell by, or, when it did not fall, the credit that moved between bills.
    const balanceFell = -parts.closingP;
    const headline = fromPaise(balanceFell > 0 ? balanceFell : S);
    const unread = ctx.unread.filter(v => v.sourceKey === parts.sourceKey);
    const unreadP = unread.reduce((s, v) => s + -toPaise(v.partyAmount), 0);

    const event = (over: Partial<CreditEvent> & Pick<CreditEvent, 'status' | 'explanation' | 'amount'>, v?: VoucherState): CreditEvent => ({
        id: v ? `v:${v.guid.toLowerCase()}:${v.alterId ?? 'na'}` : mId(),
        sourceKey: parts.sourceKey,
        evidence: v ? 'voucher' : 'movement',
        kind: 'credit',
        ...(v ? { voucher: voucherRef(v), effectiveDate: v.date } : { between, effectiveDate: ctx.asOf }),
        allocations: [], unallocated: [],
        ...over,
    } as CreditEvent);

    // Vouchers whose bill lines were not read: each needs exactly one explanation, or it is unclear.
    const ambiguousForUnread = (explanation: AllocationExplanation) => {
        for (const v of unread) credits.push(event({ status: 'AMBIGUOUS', explanation, amount: fromPaise(-toPaise(v.partyAmount)), observed }, v));
    };
    // The vouchers read for this period explain none of what is left, so it is no receipt or credit Timely saw. An
    // invoice deleted or reduced in TallyPrime moves its bills exactly as a receipt against it would (proved on
    // TallyPrime 7.1, 5 Oct 2026): said plainly, never derived.
    const noVoucher = ctx.vouchersRead && unread.length === 0;
    const notExplained = (amountP: number) => event({ status: 'AMBIGUOUS', explanation: 'ambiguous_no_voucher', amount: fromPaise(amountP), observed, kind: balanceFell > 0 ? 'credit' : 'allocation_change' });

    if (!parts.billWise) {
        if (Q > 0 && noVoucher) credits.push(notExplained(Q));
        else if (Q > 0) {
            if (unread.length === 1 && unreadP === Q) credits.push(event({ status: 'UNALLOCATED_FROM_TALLY', explanation: 'ledger_not_bill_wise', amount: fromPaise(Q), unallocated: [{ kind: 'not_bill_wise', amount: fromPaise(Q) }] }, unread[0]));
            else if (unread.length > 1 && unreadP === Q) {
                for (const v of unread) { const a = fromPaise(-toPaise(v.partyAmount)); credits.push(event({ status: 'UNALLOCATED_FROM_TALLY', explanation: 'ledger_not_bill_wise', amount: a, unallocated: [{ kind: 'not_bill_wise', amount: a }] }, v)); }
            } else credits.push(event({ status: 'UNALLOCATED_FROM_TALLY', explanation: 'ledger_not_bill_wise', amount: fromPaise(Q), unallocated: [{ kind: 'not_bill_wise', amount: fromPaise(Q) }] }));
        }
        return { credits, reversals };
    }

    if (parts.identityUncertain && (S > 0 || R > 0)) {
        if (unread.length) ambiguousForUnread('ambiguous_bill_identity');
        else credits.push(event({ status: 'AMBIGUOUS', explanation: 'ambiguous_bill_identity', amount: headline, observed, kind: balanceFell > 0 ? 'credit' : 'allocation_change' }));
        return { credits, reversals };
    }

    if (R > 0) {
        if (S > 0) {
            if (unread.length) ambiguousForUnread('ambiguous_mixed_movement');
            else credits.push(event({ status: 'AMBIGUOUS', explanation: 'ambiguous_mixed_movement', amount: headline, observed, kind: balanceFell > 0 ? 'credit' : 'allocation_change' }));
            return { credits, reversals };
        }
        reversals.push({
            id: mId(), sourceKey: parts.sourceKey, evidence: 'movement', kind: 'bill_reopened', amount: fromPaise(R),
            bills: parts.reopened.map(r => ({ ref: r.ref, billKey: r.key, amount: fromPaise(r.p) })), between, effectiveDate: ctx.asOf,
        });
        if (Q > 0) credits.push(...(noVoucher ? [notExplained(Q)] : unallocatedCredit()));
        return { credits, reversals };
    }

    function unallocatedCredit(): CreditEvent[] {
        const pieces: CreditEvent['unallocated'] = [
            ...parts.creditAdded.map(c => ({ kind: 'credit_reference' as const, ref: c.ref, amount: fromPaise(c.p) })),
            ...(uDown > 0 ? [{ kind: 'on_account' as const, amount: fromPaise(uDown) }] : []),
        ];
        const piecesP = pieces.reduce((s, x) => s + toPaise(x.amount), 0);
        if (piecesP !== Q) {
            if (unread.length) { ambiguousForUnread('ambiguous_amounts_disagree'); return []; }
            return [event({ status: 'AMBIGUOUS', explanation: 'ambiguous_amounts_disagree', amount: fromPaise(Q), observed })];
        }
        if (unread.length === 0) return [event({ status: 'UNALLOCATED_FROM_TALLY', explanation: 'derived_unallocated', amount: fromPaise(Q), unallocated: pieces })];
        if (unread.length === 1 && unreadP === Q) return [event({ status: 'UNALLOCATED_FROM_TALLY', explanation: 'derived_unallocated', amount: fromPaise(Q), unallocated: pieces }, unread[0])];
        ambiguousForUnread('ambiguous_several_receipts');
        return [];
    }

    if (S === 0) {
        if (Q > 0) credits.push(...(noVoucher ? [notExplained(Q)] : unallocatedCredit()));
        else if (Q < 0 && (uUp > 0 || CU > 0) && N === 0) {
            reversals.push({ id: mId(), sourceKey: parts.sourceKey, evidence: 'movement', kind: 'unallocated_credit_removed', amount: fromPaise(-Q), bills: parts.creditUsed.map(c => ({ ref: c.ref, billKey: c.key, amount: fromPaise(c.p) })), between, effectiveDate: ctx.asOf });
        }
        return { credits, reversals };
    }

    // Some debit bills fell.
    const lines = (p: Parts['settled']) => p.map(s => ({ ref: s.ref, billKey: s.key, amount: fromPaise(s.p) }));
    const existing = CU + uUp;
    const unallocatedPieces: CreditEvent['unallocated'] = [
        ...parts.creditAdded.map(c => ({ kind: 'credit_reference' as const, ref: c.ref, amount: fromPaise(c.p) })),
        ...(uDown > 0 ? [{ kind: 'on_account' as const, amount: fromPaise(uDown) }] : []),
    ];
    // With credit already held, a fall with no new money is that credit applied — a deleted invoice would lower the
    // balance — so it stays derived below. Any new money no voucher read explains is said plainly.
    if (noVoucher && Q > 0) {
        credits.push(notExplained(Q));
        return { credits, reversals };
    }
    if (existing === 0) {
        // All of it new credit: the bills that fell are where it went. One explanation — unless several unread vouchers share it.
        if (unread.length <= 1) {
            if (unread.length === 1 && unreadP !== Q) { ambiguousForUnread('ambiguous_amounts_disagree'); return { credits, reversals }; }
            credits.push(event({ status: 'DERIVED_WITH_HIGH_CONFIDENCE', explanation: unread.length ? 'derived_for_voucher' : 'derived_single_explanation', amount: fromPaise(Q), allocations: lines(parts.settled), unallocated: unallocatedPieces }, unread[0]));
            return { credits, reversals };
        }
        if (parts.settled.length === 1 && unallocatedPieces.length === 0 && unreadP === Q) {
            // Several vouchers, one bill: every one of them went to it.
            for (const v of unread) { const a = fromPaise(-toPaise(v.partyAmount)); credits.push(event({ status: 'DERIVED_WITH_HIGH_CONFIDENCE', explanation: 'derived_for_voucher', amount: a, allocations: [{ ref: parts.settled[0]!.ref, billKey: parts.settled[0]!.key, amount: a }] }, v)); }
            return { credits, reversals };
        }
        ambiguousForUnread('ambiguous_several_receipts');
        return { credits, reversals };
    }
    if (Q <= 0) {
        // No new money: credit already held (on account, an advance) was applied to the bills that fell.
        credits.push(event({ kind: 'allocation_change', status: 'DERIVED_WITH_HIGH_CONFIDENCE', explanation: 'derived_allocation_applied', amount: fromPaise(S), allocations: lines(parts.settled) }));
        return { credits, reversals };
    }
    // New money and credit already held, both applied.
    if (parts.settled.length === 1 && unread.length <= 1) {
        const only = parts.settled[0]!;
        const fromNew = Math.min(Q, only.p);
        credits.push(event({ status: 'DERIVED_WITH_HIGH_CONFIDENCE', explanation: unread.length ? 'derived_for_voucher' : 'derived_single_explanation', amount: fromPaise(Q), allocations: [{ ref: only.ref, billKey: only.key, amount: fromPaise(fromNew) }], unallocated: unallocatedPieces }, unread[0]));
        if (only.p > fromNew) credits.push(event({ kind: 'allocation_change', status: 'DERIVED_WITH_HIGH_CONFIDENCE', explanation: 'derived_allocation_applied', amount: fromPaise(only.p - fromNew), allocations: [{ ref: only.ref, billKey: only.key, amount: fromPaise(only.p - fromNew) }] }));
        return { credits, reversals };
    }
    if (unread.length) ambiguousForUnread('ambiguous_earlier_credit');
    else credits.push(event({ status: 'AMBIGUOUS', explanation: 'ambiguous_earlier_credit', amount: fromPaise(Q), observed }));
    return { credits, reversals };
}

/* ---------------------------- reconciliation ---------------------------- */

const stateOf = (v: SourceVoucherFact): VoucherState => ({
    guid: v.guid, ...(v.alterId !== undefined ? { alterId: v.alterId } : {}), sourceKey: v.sourceKey,
    date: v.date, type: v.type, ...(v.number ? { number: v.number } : {}),
    partyAmount: v.partyAmount, allocations: v.allocations.map(a => ({ ...a })), allocationsRead: v.allocationsRead,
    cancelled: v.cancelled, optional: v.optional, postDated: v.postDated,
    ...(v.instrument ? { instrument: { ...v.instrument } } : {}),
});

const sameAllocations = (a: VoucherBillAllocation[], b: VoucherBillAllocation[]) => {
    const key = (x: VoucherBillAllocation[]) => x.map(l => `${l.kind}|${normRef(l.ref)}|${toPaise(l.amount)}`).sort().join(';');
    return key(a) === key(b);
};

function changeKind(before: VoucherState, after: VoucherState): SourceChangeKind | null {
    if (!before.cancelled && after.cancelled) return 'cancelled';
    if (before.cancelled && !after.cancelled) return 'restored';
    if (toPaise(before.partyAmount) !== toPaise(after.partyAmount)) return 'amount_changed';
    if (before.allocationsRead && after.allocationsRead && !sameAllocations(before.allocations, after.allocations)) return 'allocation_changed';
    if (before.date !== after.date) return 'date_changed';
    return null;
}

/**
 * Everything one read tells Timely about money moving, against the read
 * before it and the vouchers remembered from earlier reads.
 *
 * 1. Vouchers (when read): each crediting voucher is classified from its own
 *    lines; an altered voucher is a source change, recorded beside its new
 *    classification — the old one is not rewritten.
 * 2. Movement (from the second read on): each account's bills before and
 *    after, less what the vouchers already explain. What is left is
 *    classified as derived, unallocated or ambiguous, and never guessed.
 */
export function reconcileSource(input: ReconcileInput): ReconcileResult {
    const { prev, curr } = input;
    const accountsNow = new Map(curr.accounts.map(a => [a.sourceKey, a]));
    const known = new Map((input.knownVouchers ?? []).map(v => [v.guid.toLowerCase(), v]));
    const credits: CreditEvent[] = [], reversals: ReversalEvent[] = [], changes: SourceChange[] = [];
    const companyChanged = Boolean(prev && prev.source.companyId.toLowerCase() !== curr.source.companyId.toLowerCase());
    if (companyChanged) return { credits, reversals, changes, voucherStates: [], unexplained: [], companyChanged, closedNow: [] };

    const vouchersRead = curr.vouchers !== undefined;
    const now = new Map((curr.vouchers ?? []).map(v => [v.guid.toLowerCase(), stateOf(v)]));
    const prevAsOf = prev?.asOf ?? null;
    // Whether the last read's vouchers covered a date: if they did, a voucher of that date first seen now is new since
    // then; if they did not (vouchers were not read then — a connector switching the read on — or it is older than what
    // was read), it was already in TallyPrime if dated by then, and its effect is in the last read's bills.
    const prevCovered = (date: string) => Boolean(prev?.voucherWindow && date >= prev.voucherWindow.from && date <= prev.voucherWindow.to);
    // Whether this read's vouchers cover the whole time between the two reads: only then can "no voucher explains it" be said.
    const windowCovers = Boolean(vouchersRead && prevAsOf && curr.voucherWindow && curr.voucherWindow.from <= prevAsOf && curr.voucherWindow.to >= curr.asOf);

    // 1. Vouchers.
    const unreadByAccount: VoucherState[] = [];
    const expected = new Map<string, Effect | null>();
    const addExpected = (sourceKey: string, e: Effect | null, sign: 1 | -1) => {
        if (!expected.has(sourceKey)) expected.set(sourceKey, emptyEffect());
        const acc = expected.get(sourceKey);
        if (acc === null) return;
        if (e === null) { expected.set(sourceKey, null); return; }
        acc!.closingP += sign * e.closingP;
        acc!.unbilledP += sign * e.unbilledP;
        for (const [k, b] of e.bills) {
            const cur = acc!.bills.get(k);
            acc!.bills.set(k, { ref: b.ref, amountP: (cur?.amountP ?? 0) + sign * b.amountP });
        }
    };
    for (const [guid, v] of now) {
        const before = known.get(guid);
        if (before) {
            const kind = changeKind(before, v);
            if (kind) changes.push({ id: `c:${guid}:${v.alterId ?? 'na'}`, sourceKey: v.sourceKey, voucherGuid: v.guid, change: kind, before, after: v });
        }
        // What this read adds to the movement since the last one.
        if (prevAsOf) {
            const assumedOld = !before && v.date <= prevAsOf && !prevCovered(v.date);
            const was = before ? effectAt(before, prevAsOf) : assumedOld ? effectAt(v, prevAsOf) : emptyEffect();
            const is = effectAt(v, curr.asOf);
            if (was === null || is === null) { if (!assumedOld && !v.allocationsRead && !v.cancelled && !v.optional && v.date <= curr.asOf && toPaise(v.partyAmount) < 0 && (!before || before.alterId !== v.alterId || before.date > prevAsOf)) unreadByAccount.push(v); }
            else { addExpected(v.sourceKey, is, 1); addExpected(v.sourceKey, was, -1); }
        }
        // A voucher is classified when first seen and again when altered; a post-dated one is not re-announced on its date.
        const isNewOrChanged = !before || before.alterId !== v.alterId || changeKind(before, v) !== null;
        if (before && !before.cancelled && !before.optional && v.cancelled && toPaise(before.partyAmount) < 0) reversals.push(voucherReversal(before, v.date));
        if (!isNewOrChanged || v.cancelled || v.optional) continue;
        if (toPaise(v.partyAmount) < 0) {
            const ev = classifyCreditVoucher(v, accountsNow.get(v.sourceKey));
            if (ev && before && (before.alterId !== v.alterId)) ev.supersedes = `v:${guid}:${before.alterId ?? 'na'}`;
            if (ev) credits.push(ev);
            else if (!prevAsOf) {
                // First read: nothing to derive from. Said plainly rather than guessed.
                credits.push({ id: `v:${guid}:${v.alterId ?? 'na'}`, sourceKey: v.sourceKey, evidence: 'voucher', kind: 'credit', voucher: voucherRef(v), effectiveDate: v.date, amount: fromPaise(-toPaise(v.partyAmount)), status: 'AMBIGUOUS', allocations: [], unallocated: [], explanation: 'ambiguous_lines_not_read' });
            }
        } else if (toPaise(v.partyAmount) > 0 && v.allocations.some(a => a.kind === 'agst_ref') && v.allocationsRead) {
            // A debit entered against an existing bill: a cheque returned, a receipt reversed, a debit note.
            const lines = v.allocations.filter(a => a.kind === 'agst_ref' && toPaise(a.amount) > 0);
            reversals.push({
                id: `v:${guid}:${v.alterId ?? 'na'}`, sourceKey: v.sourceKey, evidence: 'voucher', kind: 'bill_reopened',
                amount: fromPaise(lines.reduce((s, a) => s + toPaise(a.amount), 0)),
                bills: lines.map(a => ({ ref: a.ref, amount: a.amount })), voucher: voucherRef(v), effectiveDate: v.date,
            });
        }
    }
    // Remembered vouchers inside this read's window that are no longer there.
    if (vouchersRead && curr.voucherWindow) {
        for (const [guid, before] of known) {
            if (now.has(guid) || before.date < curr.voucherWindow.from || before.date > curr.voucherWindow.to) continue;
            changes.push({ id: `c:${guid}:gone`, sourceKey: before.sourceKey, voucherGuid: before.guid, change: 'no_longer_in_tally', before, after: null });
            if (!before.cancelled && !before.optional && toPaise(before.partyAmount) < 0) reversals.push(voucherReversal(before, curr.asOf));
            if (prevAsOf) addExpected(before.sourceKey, effectAt(before, prevAsOf), -1);
        }
    }

    // 2. Movement.
    const unexplained: ReconcileResult['unexplained'] = [];
    const closedNow: ClosedBill[] = [];
    if (prev) {
        const cmp = compareFacts(prev, curr);
        const remembered = new Set((input.recentlyClosed ?? []).map(c => `${c.sourceKey}|${c.key}|${c.billDate}`));
        let n = 0;
        const seq = () => ++n;
        for (const m of cmp.accounts) {
            if (!m.seenBefore || !m.seenAfter) continue;
            for (const d of m.bills) {
                // A bill that closed earlier and is open again is pending again, not a new sale.
                if (d.status === 'new' && d.side === 'Dr' && remembered.has(`${m.sourceKey}|${d.key}|${d.billDate}`)) d.status = 'increased';
                if (d.status === 'closed' && d.side === 'Dr') closedNow.push({ sourceKey: m.sourceKey, key: d.key, ref: d.ref, billDate: d.billDate, amount: d.before, closedAt: curr.asOf });
            }
            const exp = vouchersRead ? (expected.has(m.sourceKey) ? expected.get(m.sourceKey)! : emptyEffect()) : null;
            const parts = partsOf(m, exp);
            if (vouchersRead && exp) {
                const explained = [...exp.bills.values()].reduce((s, b) => s + Math.max(0, -b.amountP), 0);
                if (sumP(parts.settled) > 0 || sumP(parts.reopened) > 0) unexplained.push({ sourceKey: m.sourceKey, observedSettled: m.debitSettled, explainedByVouchers: fromPaise(explained) });
            }
            const nothingLeft = parts.settled.length + parts.reopened.length + parts.creditAdded.length + parts.creditUsed.length === 0
                && parts.unbilledP === 0 && parts.closingP - sumP(parts.sales) === 0;
            const unread = unreadByAccount.filter(v => v.sourceKey === m.sourceKey);
            if (nothingLeft && unread.length === 0) continue;
            const r = classifyParts(parts, { from: prev.capturedAt, to: curr.capturedAt, asOf: curr.asOf, seq, unread, vouchersRead: windowCovers });
            credits.push(...r.credits);
            reversals.push(...r.reversals);
        }
    }

    // Remember: every voucher read now, and those remembered from outside this read's window.
    const voucherStates = vouchersRead
        ? [...now.values(), ...[...known.values()].filter(k => !now.has(k.guid.toLowerCase()) && curr.voucherWindow && (k.date < curr.voucherWindow.from || k.date > curr.voucherWindow.to))]
        : [...known.values()];
    return { credits, reversals, changes, voucherStates, unexplained, companyChanged, closedNow };
}

/** A crediting voucher that TallyPrime no longer has, or has cancelled: what it settled is pending again. */
function voucherReversal(before: VoucherState, effectiveDate: string): ReversalEvent {
    const lines = before.allocations.filter(a => a.kind === 'agst_ref' && toPaise(a.amount) < 0);
    return {
        id: `r:${before.guid.toLowerCase()}:${before.alterId ?? 'na'}`,
        sourceKey: before.sourceKey,
        evidence: 'voucher',
        kind: lines.length ? 'bill_reopened' : 'unallocated_credit_removed',
        amount: fromPaise(-toPaise(before.partyAmount)),
        bills: lines.map(a => ({ ref: a.ref, amount: fromPaise(-toPaise(a.amount)) })),
        voucher: voucherRef(before),
        effectiveDate,
    };
}
